/**
 * Voyager ↔ Figma component map.
 *
 * The machine-readable index of which Voyager component corresponds to which node in the
 * Journey B2C Design System (`uVS4HTaSxt861vr1XotjbW`). Use it to walk Voyager against Figma
 * when auditing for design drift — it saves rediscovering these relationships each time.
 *
 * `codeConnect: false` means the relationship is real but NOT publishable via
 * `figma connect publish`, because another Voyager component is the canonical owner of that
 * node. Filter on this flag when publishing; ignore it when auditing.
 *
 * Generated from the `.figma.tsx` files on disk. See
 * `docs/design-docs/voyager-figma-code-connect-audit.md` for the rules behind each decision.
 */

export type FigmaRelation = 'owns' | 'variant-of' | 'shares-node' | 'section-of' | 'voyager-only';

export interface FigmaComponentMapEntry {
  /** Figma node id, or null when the component has no Design System counterpart. */
  node: string | null;
  /** Human-readable Figma component name, for grepping the design file. */
  figmaName: string | null;
  relation: FigmaRelation;
  /** Whether this mapping is safe to publish via `figma connect publish`. */
  codeConnect: boolean;
  /** Figma variant restriction applied by the mapping, if any. */
  variant?: string;
  /** Figma variant properties consumed as code props via `figma.enum`. */
  enums?: string[];
}

export const figmaComponentMap: Record<string, FigmaComponentMapEntry> = {
  // components — 72 mapped, 50 publishable
  'components/AccommodationItemCard': {
    node: '1327:25231',
    figmaName: 'Rate Card',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/AccordionLink': {
    node: '314:1940',
    figmaName: 'Modal Trigger',
    relation: 'owns',
    codeConnect: true,
  },
  'components/AccountBookingCard': {
    node: '2694:5564',
    figmaName: '.BuildingBlocks/Account Booking Card',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Default'",
  },
  'components/AccountCTACard': {
    node: '2694:6619',
    figmaName: '.BuildingBlocks/Account CTA Card',
    relation: 'owns',
    codeConnect: true,
  },
  'components/BackButton': {
    node: '1327:30814',
    figmaName: 'Buttons (compact instance)',
    relation: 'owns',
    codeConnect: true,
  },
  'components/Badge': {
    node: '1327:25723',
    figmaName: 'Badge',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'components/BasketCard': {
    node: '1435:18681',
    figmaName: 'Basket Card',
    relation: 'owns',
    codeConnect: true,
  },
  'components/BrandHeader': {
    node: '1308:24047',
    figmaName: 'Logo Header',
    relation: 'owns',
    codeConnect: true,
  },
  'components/Button': {
    node: '116:1330',
    figmaName: 'Buttons',
    relation: 'owns',
    codeConnect: true,
    enums: ['Size', 'State', 'Style'],
  },
  'components/Calendar': {
    node: '1322:12364',
    figmaName: 'Calendar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Default'",
  },
  'components/CalendarDay': {
    node: '646:31508',
    figmaName: '.Buildingblocks/Calendar Cells',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/CardGrid': {
    node: '813:1067',
    figmaName: 'Card Grid',
    relation: 'owns',
    codeConnect: true,
  },
  'components/Carousel': {
    node: '1367:41852',
    figmaName: 'Carousel',
    relation: 'owns',
    codeConnect: true,
  },
  'components/CarouselControls': {
    node: '1315:17345',
    figmaName: 'Carousel Controls',
    relation: 'owns',
    codeConnect: true,
  },
  'components/Checkbox': {
    node: '1368:46879',
    figmaName: 'Checkbox',
    relation: 'owns',
    codeConnect: true,
    enums: ['State', 'Type'],
  },
  'components/ContentArea': {
    node: '1332:61779',
    figmaName: 'Template/100%',
    relation: 'shares-node',
    codeConnect: false,
  },
  'components/ContentParser': {
    node: '2571:49877',
    figmaName: 'Privacy Text',
    relation: 'section-of',
    codeConnect: true,
  },
  'components/Counter': {
    node: '1935:35958',
    figmaName: 'Counter',
    relation: 'owns',
    codeConnect: true,
  },
  'components/DayStripSelector': {
    node: '1394:17858',
    figmaName: 'Inline Date Picker',
    relation: 'owns',
    codeConnect: true,
  },
  'components/Divider': {
    node: '2862:19647',
    figmaName: '.buildingblocks/checkout/dividers',
    relation: 'owns',
    codeConnect: true,
    enums: ['Size'],
  },
  'components/Drawer': {
    node: '3087:18022',
    figmaName: 'Bottom Sheet',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Default'",
  },
  'components/Dropdown': {
    node: '1614:27776',
    figmaName: 'Filter Dropdown',
    relation: 'owns',
    codeConnect: true,
  },
  'components/EmptyMessage': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/EntityImage': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/ErrorText': {
    node: '1368:45468',
    figmaName: 'Input Field',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/ExpandableSection': {
    node: '314:1940',
    figmaName: 'Modal Trigger',
    relation: 'shares-node',
    codeConnect: false,
  },
  'components/ExpandableText': {
    node: '297:1555',
    figmaName: 'Detail Description',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/FeaturedBanner': {
    node: '1615:31035',
    figmaName: 'CTA Card',
    relation: 'owns',
    codeConnect: true,
  },
  'components/FilterPill': {
    node: '1365:30849',
    figmaName: 'Filter',
    relation: 'owns',
    codeConnect: true,
  },
  'components/FloatingField': {
    node: '1368:45468',
    figmaName: 'Input Field',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/FormCard': {
    node: '1368:46223',
    figmaName: 'Input Form',
    relation: 'owns',
    codeConnect: true,
  },
  'components/FormField': {
    node: '1368:45468',
    figmaName: 'Input Field',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/FormLayout': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/GuestPicker': {
    node: '1324:45194',
    figmaName: 'Guest Selector',
    relation: 'owns',
    codeConnect: true,
  },
  'components/ImageFrame': {
    node: '1128:8',
    figmaName: 'Image',
    relation: 'owns',
    codeConnect: true,
  },
  'components/ImageGallery': {
    node: '1172:3066',
    figmaName: 'Image Actions',
    relation: 'owns',
    codeConnect: true,
  },
  'components/InfoNote': {
    node: '1480:53618',
    figmaName: 'Toast',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'components/Input': {
    node: '1368:45468',
    figmaName: 'Input Field',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/InputGroup': {
    node: '1368:46220',
    figmaName: 'Input Groups',
    relation: 'owns',
    codeConnect: true,
  },
  'components/ItemCard': {
    node: '1317:13223',
    figmaName: 'Card',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/Layout': {
    node: '1332:61779',
    figmaName: 'Template/100%',
    relation: 'shares-node',
    codeConnect: false,
  },
  'components/ListItemCard': {
    node: '1904:10125',
    figmaName: 'Treatment Card',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/MobileTabBar': {
    node: '1303:24407',
    figmaName: 'Navigation Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Device: 'Mobile'",
  },
  'components/Modal': {
    node: '1425:16279',
    figmaName: 'Pop Up',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Default'",
  },
  'components/ModalTotalFooter': {
    node: '1327:30677',
    figmaName: 'Bottom Action Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Clear'",
  },
  'components/NotificationBox': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/OptionRow': {
    node: '1324:47133',
    figmaName: 'Select Field',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Default'",
    enums: ['State'],
  },
  'components/PageHeading': {
    node: '1330:54401',
    figmaName: 'Page Header',
    relation: 'owns',
    codeConnect: true,
  },
  'components/PanelCard': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/PaymentStepSkeleton': {
    node: '2851:15223',
    figmaName: '.buildingblocks/checkout/payment method',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/PhoneInput': {
    node: '1368:46220',
    figmaName: 'Input Groups',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Stack: 'Horizontal'",
  },
  'components/PriceDisplay': {
    node: '784:16152',
    figmaName: '.buildingblocks/card details',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/RadioButton': {
    node: '1368:46895',
    figmaName: 'Radio Button',
    relation: 'owns',
    codeConnect: true,
    enums: ['State', 'Type'],
  },
  'components/RadioGroup': {
    node: '1614:26629',
    figmaName: 'Interactive Field List',
    relation: 'owns',
    codeConnect: true,
  },
  'components/ReservationCalendar': {
    node: '1322:12364',
    figmaName: 'Calendar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'With prices'",
  },
  'components/Section': {
    node: '1332:61779',
    figmaName: 'Template/100%',
    relation: 'shares-node',
    codeConnect: false,
  },
  'components/SectionHeader': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/SectionHeading': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/SelectablePill': {
    node: '1420:40140',
    figmaName: 'Chip',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/SideImage': {
    node: '1128:8',
    figmaName: 'Image',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/SideNav': {
    node: '1303:24407',
    figmaName: 'Navigation Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Device: 'Desktop'",
  },
  'components/SkeletonBlock': {
    node: '1128:8',
    figmaName: 'Image',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/SkeletonCard': {
    node: '1317:13223',
    figmaName: 'Card',
    relation: 'section-of',
    codeConnect: false,
  },
  'components/Spinner': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/StepProgress': {
    node: '1335:26463',
    figmaName: 'Progress Indicator',
    relation: 'owns',
    codeConnect: true,
    enums: ['State'],
  },
  'components/StickyActionBar': {
    node: '1327:30677',
    figmaName: 'Bottom Action Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Single button'",
  },
  'components/StickyFooter': {
    node: '1327:30677',
    figmaName: 'Bottom Action Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Text & button'",
  },
  'components/TextArea': {
    node: '1368:45468',
    figmaName: 'Input Field',
    relation: 'shares-node',
    codeConnect: false,
    enums: ['State'],
  },
  'components/TextButton': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'components/TimeslotChipSelector': {
    node: '1879:17880',
    figmaName: 'Chip Carousel',
    relation: 'owns',
    codeConnect: true,
  },
  'components/TimeslotRow': {
    node: '1329:37319',
    figmaName: 'List Item',
    relation: 'section-of',
    codeConnect: true,
  },
  'components/ViewToggle': {
    node: '1438:22546',
    figmaName: 'Tab Bar',
    relation: 'owns',
    codeConnect: true,
  },

  // composites — 48 mapped, 31 publishable
  'composites/ActionBar': {
    node: '1400:11324',
    figmaName: 'Top Action Bar',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/AccommodationContentPanel': {
    node: '1327:26223',
    figmaName: 'Rate List',
    relation: 'section-of',
    codeConnect: true,
  },
  'composites/AccountSideMenu': {
    node: '1390:78433',
    figmaName: 'Select Field List',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/AllOptionsTimeslotPanel': {
    node: '1394:18380',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/AnnouncementBanner': {
    node: '1332:62629',
    figmaName: 'Promotional Banner',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/AnnouncementDialog': {
    node: '1332:61654',
    figmaName: 'Pop up Text',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Promotional'",
  },
  'composites/AppShell': {
    node: '1332:61779',
    figmaName: 'Template/100%',
    relation: 'shares-node',
    codeConnect: false,
  },
  'composites/BasketContents': {
    node: '1435:20428',
    figmaName: 'Basket Itemised',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/BillingDetailsForm': {
    node: '2860:25386',
    figmaName: '.buildingblocks/checkout/personal details',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'composites/BookingNotesCard': {
    node: '2516:35036',
    figmaName: 'Add any notes card (Guest Details Form)',
    relation: 'section-of',
    codeConnect: true,
  },
  'composites/BookingNotificationContent': {
    node: '3082:5532',
    figmaName: 'Dialog',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Information'",
  },
  'composites/BookingRequestModal': {
    node: '1425:16279',
    figmaName: 'Pop Up',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Dialog'",
  },
  'composites/BookingSummaryRow': {
    node: '784:16152',
    figmaName: '.buildingblocks/card details',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Title and Description'",
  },
  'composites/CheckoutSummaryMobile': {
    node: '1435:20428',
    figmaName: 'Basket Itemised',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/CheckoutSummaryPanel': {
    node: '1435:20428',
    figmaName: 'Basket Itemised',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/ContentPanel': {
    node: '297:1555',
    figmaName: 'Detail Description',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/DatePicker': {
    node: '1318:24602',
    figmaName: 'Date Picker',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'composites/DeliveryStep': {
    node: '2851:16320',
    figmaName: '.buildingblocks/checkout/delivery method',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Click and collect'",
  },
  'composites/DepositPaymentPicker': {
    node: '2851:15815',
    figmaName: '.buildingblocks/checkout/payment options',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/DetailModal': {
    node: '1425:16279',
    figmaName: 'Pop Up',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Full width'",
  },
  'composites/DiscountCode': {
    node: '2650:9705',
    figmaName: 'Promotion Code Toast',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/ExtrasCard': {
    node: '1317:13223',
    figmaName: 'Card',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/ExtrasEditModal': {
    node: '3224:17863',
    figmaName: '.BuildingBlocks/Extras Pop-up',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'composites/GuestDetailsContentPanel': {
    node: '1925:25244',
    figmaName: 'Guest Details Form',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/GuestDetailsPanel': {
    node: '1925:25244',
    figmaName: 'Guest Details Form',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/GuestSelectModal': {
    node: '1614:26629',
    figmaName: 'Interactive Field List',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/ItineraryView': {
    node: '1454:33761',
    figmaName: 'Basket Itinerary',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/JourneyPicker': {
    node: '723:9727',
    figmaName: 'Search Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Device: 'Desktop'",
    enums: ['Function'],
  },
  'composites/LanguagePicker': {
    node: '1324:47276',
    figmaName: 'Language Selector',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/MenuDrawer': {
    node: '2569:45627',
    figmaName: 'Navigation Sheet',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/MobileJourneyPicker': {
    node: '723:9727',
    figmaName: 'Search Bar',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Device: 'Mobile'",
  },
  'composites/MultiOptionTimeslotPanel': {
    node: '1394:18380',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/PaymentMethodForm': {
    node: '2851:15223',
    figmaName: '.buildingblocks/checkout/payment method',
    relation: 'shares-node',
    codeConnect: false,
  },
  'composites/PreferencesPanel': {
    node: '1420:41667',
    figmaName: 'Chip Group',
    relation: 'section-of',
    codeConnect: true,
  },
  'composites/ProductContentPanel': {
    node: '1935:36086',
    figmaName: 'Product Selector',
    relation: 'section-of',
    codeConnect: true,
  },
  'composites/Recommendations': {
    node: '1332:60025',
    figmaName: 'CTA Grid',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/ReservationPanel': {
    node: '1480:42018',
    figmaName: 'Sub Header',
    relation: 'section-of',
    codeConnect: true,
  },
  'composites/RoomUpgradeDialog': {
    node: '1332:61654',
    figmaName: 'Pop up Text',
    relation: 'variant-of',
    codeConnect: true,
    variant: "Type: 'Detail'",
  },
  'composites/RoomUpgradeOfferPanel': {
    node: '1615:31035',
    figmaName: 'CTA Card',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/SearchActionBar': {
    node: '1414:26458',
    figmaName: '.BuildingBlocks/Search',
    relation: 'owns',
    codeConnect: true,
    enums: ['Type'],
  },
  'composites/SignInModal': {
    node: '1480:56098',
    figmaName: 'Sign In & Up Form',
    relation: 'shares-node',
    codeConnect: false,
  },
  'composites/SingleOptionTimeslotPanel': {
    node: '1394:18380',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'section-of',
    codeConnect: false,
  },
  'composites/SpaClassCard': {
    node: '2378:17778',
    figmaName: 'Activity Card',
    relation: 'shares-node',
    codeConnect: false,
  },
  'composites/SpaClassCompactCard': {
    node: '2378:17778',
    figmaName: 'Activity Card',
    relation: 'shares-node',
    codeConnect: false,
  },
  'composites/SpaDaysBookingContent': {
    node: '1394:18380',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/SpaItemCard': {
    node: '1317:13223',
    figmaName: 'Card',
    relation: 'shares-node',
    codeConnect: false,
    enums: ['State'],
  },
  'composites/SpaTreatmentFacilityBookingContent': {
    node: '1685:18198',
    figmaName: 'Treatmens/Availability & Details Form',
    relation: 'owns',
    codeConnect: true,
  },
  'composites/SplitLayout': {
    node: '1325:16087',
    figmaName: 'Template/Detail',
    relation: 'shares-node',
    codeConnect: false,
  },

  // templates — 32 mapped, 13 publishable
  'templates/AccommodationDetailTemplate': {
    node: '1342:16689',
    figmaName: 'Template/Detail',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/AccommodationListTemplate': {
    node: '1342:16688',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/AccountBookingsTemplate': {
    node: '2667:43583',
    figmaName: 'Account Bookings Listing',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/AccountPersonalInformationTemplate': {
    node: '2667:43410',
    figmaName: 'Account Management Form',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/BasketDrawerTemplate': {
    node: '2214:17563',
    figmaName: 'Basket Itemised',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/BasketTemplate': {
    node: '1475:26701',
    figmaName: 'Basket Itemised',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/CheckoutTemplate': {
    node: '1479:32048',
    figmaName: 'Template/Checkout',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/ConfirmationTemplate': {
    node: '1479:36027',
    figmaName: 'Checkout/Confirmation',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/DeferredPaymentTemplate': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'templates/DiningListTemplate': {
    node: '1416:32103',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/DiningReservationTemplate': {
    node: '1416:36322',
    figmaName: 'Dining/Reservation Form',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/ExtrasTemplate': {
    node: '1614:21812',
    figmaName: '.BuildingBlocks/Extras Pop-up',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/ForgottenPasswordTemplate': {
    node: null,
    figmaName: null,
    relation: 'voyager-only',
    codeConnect: false,
  },
  'templates/GuestDetailsTemplate': {
    node: '1479:33170',
    figmaName: 'Guest Details Form',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/HomeTemplate': {
    node: '2705:4171',
    figmaName: 'Template/100%',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/MyAccountTemplate': {
    node: '2665:38573',
    figmaName: 'Template/Account',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/OrderDetailTemplate': {
    node: '2679:56428',
    figmaName: 'Account Booking Detail',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/ProductDetailTemplate': {
    node: '1935:37072',
    figmaName: 'Template/Detail',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/ProductListTemplate': {
    node: '1935:33471',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/RoomUpgradeTemplate': {
    node: '1342:16690',
    figmaName: 'Template/Detail',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SearchFlowTemplate': {
    node: '2553:9857',
    figmaName: 'Template/100%',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SignInTemplate': {
    node: '1492:25528',
    figmaName: 'Sign In & Up Form',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/SpaCategoryDetailTemplate': {
    node: '1904:25243',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaCategoryListTemplate': {
    node: '1639:3161',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaClassBookingForm': {
    node: '1925:22947',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaClassListTemplate': {
    node: '2207:15958',
    figmaName: 'Schedule Card Grid',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/SpaDayDetailTemplate': {
    node: '1367:43441',
    figmaName: 'Template/Detail',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaDaysBookingForm': {
    node: '1389:71288',
    figmaName: 'Spa Day/Options & Inclusions Form',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaLandingTemplate': {
    node: '2009:16595',
    figmaName: 'Template/100%',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaListTemplate': {
    node: '1367:43442',
    figmaName: 'Template/Listing',
    relation: 'owns',
    codeConnect: false,
  },
  'templates/SpaTreatmentFacilityBookingForm': {
    node: '1904:34926',
    figmaName: 'Facilities/Availability & Details Form',
    relation: 'owns',
    codeConnect: true,
  },
  'templates/SpaTreatmentFacilityDetailTemplate': {
    node: '1880:32672',
    figmaName: 'Template/Detail',
    relation: 'owns',
    codeConnect: false,
  },
};
